import { BootstrapBreakpoint } from './theme';

// we need to only and specifically import the type here from types to prevent webpack externals definition from taking over and adding `Swiper` into the compiled script
import type { Swiper as SwiperType } from 'swiper/types';

/**
 * Throttle any events.
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 4.0.0
 * @since 5.3.0 Implemented in typescript
 *
 * @export
 * @see https://css-tricks.com/debouncing-throttling-explained-examples/#requestanimationframe-raf
 * @example throttle('scroll', 'optimizedScroll'); window.addEventListener('optimizedScroll', callback);
 */
export function throttle(type: string, name: string, obj?: Window | HTMLElement | null): void {
    obj = obj || window;
    let running: boolean = false;
    const func: () => void = () => {
        if (running) {
            return;
        }

        running = true;

        requestAnimationFrame((): void => {
            obj.dispatchEvent(new CustomEvent(name));
            running = false;
        });
    };
    obj.addEventListener(type, func);
}

/**
 * Simple function to check if an element has vertical or horizontal overflow.
 *
 * @author Kosh <kosh@cubetech.ch>
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 4.0.0
 * @since 5.3.0 Implemented in typescript
 *
 * @export
 */
export function isOverflown({ clientWidth, clientHeight, scrollWidth, scrollHeight }: HTMLElement): boolean {
    return scrollHeight > clientHeight || scrollWidth > clientWidth;
}

/**
 * Class based fading in of an element. Element needs to be hidden with the bootstrap `d-none` class. It is assumed that opacity is set to 0 using an inline style. The `fading` class is used to add a transition for opacity.
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 3.0.0
 * @since 5.3.0 Implemented in typescript
 *
 * @export
 */
export function fadeIn(element: HTMLElement): void {
    if (!element.classList.contains('fading')) {
        element.classList.add('fading');
    }

    element.classList.remove('d-none');
    setTimeout(() => {
        element.style.opacity = null;
    }, 20);
}

/**
 * Class based fading out of an element. Element will first have its opacity dropped to 0, then it will be hidden using the `d-none` bootstrap class. The `fading` class is only responsible for the opacity transition.
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 3.0.0
 * @since 5.3.0 Implemented in typescript
 * @since 5.5.0 Add optional remove
 *
 * @export
 */
export function fadeOut(element: HTMLElement, remove: boolean = false): void {
    if (!element.classList.contains('fading')) {
        element.classList.add('fading');
    }

    element.style.opacity = '0';

    element.addEventListener(
        'transitionend',
        () => {
            element.classList.add('d-none');

            if (remove) {
                element.remove();
            }
        },
        {
            capture: false,
            once: true,
            passive: false,
        }
    );
}

/**
 * Compute the scrollbar width and assign that value to a custom CSS property.
 *
 * @author Kosh <kosh@cubetech.ch>
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 4.0.0
 * @since 5.3.0 Implemented in typescript
 *
 * @export
 */
export function defineScrollbarWidth(): void {
    document.documentElement.style.setProperty(
        '--scrollbar-width',
        `${window.visualViewport.width - document.body.offsetWidth}px`
    );
}

/**
 * Get the header, measure its current height and assign the value found to a custom css property on the document element
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 4.0.0
 * @since 5.3.0 Implemented in typescript
 */
export function setHeaderHeight(): void {
    let headerHeight: number = 0;
    let fullHeaderHeight: number = 0;
    const header: HTMLElement = document.querySelector('.main-container > header');

    if (header) {
        headerHeight = header.offsetHeight;
        fullHeaderHeight = headerHeight + 120;
    }

    document.documentElement.style.setProperty('--ct-header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty('--ct-fullheader-height', `${fullHeaderHeight}px`);
}

/**
 * Helper function that converts a FormData object into a `string[][]` that then can be used to generate URLSearchParams
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 5.3.0
 */
export function prepareFormData(formdata: FormData): string[][] {
    return Array.from(formdata, ([key, value]: [string, FormDataEntryValue]): string[] => [
        key,
        typeof value === 'string' ? value : value.name,
    ]);
}

/**
 * Determine the currently active bootstrap breakpoint.
 *
 * @author Kosh <kosh@cubetech.ch>
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 3.0.0
 * @since 5.3.0 Implemented in typescript
 * @since 5.5.0 Move to utilities
 *
 * @returns Current bootstrap breakpoint name (xxl, xl, etc)
 */
export function bootstrapDetectBreakpoint(): BootstrapBreakpoint | null {
    const breakpointNames: BootstrapBreakpoint[] = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
    let breakpointValues: Record<string, string> = {};

    for (let breakpointName of breakpointNames) {
        breakpointValues[breakpointName] = window
            .getComputedStyle(document.documentElement)
            .getPropertyValue('--bs-breakpoint-' + breakpointName);
    }

    for (const breakpointName of breakpointNames) {
        if (window.matchMedia('(min-width: ' + breakpointValues[breakpointName] + ')').matches) {
            return breakpointName;
        }
    }

    return null;
}

/**
 * Get the breakpoint min-width for a given Bootstrap breakpoint. This can be changed using Sass variables which is why we have a helper to get the width dynamically.
 *
 * @author Kosh <kosh@cubetech.ch>
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.3.0
 * @since 3.0.0
 * @since 5.3.0 Implemented in typescript
 * @since 5.5.0 Moved to utilities
 *
 * @returns Defined min width for currently active bootstrap breakpoint.
 */
export function getBootstrapBreakpointWidth(): number | null {
    const breakpointNames: BootstrapBreakpoint[] = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
    let breakpointValues: Record<string, string> = {};

    for (const breakpointName of breakpointNames) {
        breakpointValues[breakpointName] = window
            .getComputedStyle(document.documentElement)
            .getPropertyValue('--bs-breakpoint-' + breakpointName);
    }

    let prevSize: string = breakpointValues['xxl'];

    for (const breakpointName of breakpointNames) {
        if (window.matchMedia('(min-width: ' + breakpointValues[breakpointName] + ')').matches) {
            return parseInt(prevSize.substring(0, prevSize.length - 2));
        }

        prevSize = breakpointValues[breakpointName];
    }

    return null;
}

/**
 * Once created a Swiper instance adds the `swiper` property to its HTML element. This seems not to be typed in the library so let's add our own interface here, so we can make use of the property without type errors.
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @version 5.7.0
 * @since 5.7.0
 * @since 5.7.0 Renamed from `CtSwiperElement` and moved to utitlities
 */
export interface CtSwiperWrapper extends HTMLElement {
    swiper: SwiperType;
}

/**
 * Type guard to check if an element exists and has the swiper property. If so the Swiper instance is accessible using that property. Put here instead of the `swiperFromConfig` to prevent conflicts with webpack externals (if we keep it there, `Swiper` gets added to the script regardless of whether we need it or not, leading to a fatal error).
 *
 * @author rafhun<rafhun@cubetech.ch>
 * @version 5.7.0
 * @since 5.7.0
 */
export function IsSwiperWrapper(element: HTMLElement | null): element is CtSwiperWrapper {
    return element && element.hasOwnProperty('swiper');
}

/**
 * Function that waits with execution of a callback until jQuery is available in the global scope. To tweak testing interval, change the timeout.
 *
 * @author rafhun<rafhun@cubetech.ch>
 * @version 5.7.0
 * @since 5.7.0
 */
export function whenJqueryAvailable(callback: () => void): void {
    if (typeof jQuery === 'undefined') {
        window.setTimeout(() => {
            whenJqueryAvailable(callback);
        }, 10);
    } else {
        callback();
    }
}

/**
 * Runtime check of whether an element is a HTML Select element.
 *
 * @author rafhun<rafhun@cubetech.ch>
 * @version 5.7.0
 * @since 5.7.0
 */
export function IsHtmlSelectElement(element: HTMLElement): element is HTMLSelectElement {
    return element.nodeName === 'SELECT';
}

export function updateURLParameter(url, param, paramVal) {
    var TheAnchor = null;
    var newAdditionalURL = '';
    var tempArray = url.split('?');
    var baseURL = tempArray[0];
    var additionalURL = tempArray[1];
    var temp = '';

    if (additionalURL) {
        var tmpAnchor = additionalURL.split('#');
        var TheParams = tmpAnchor[0];
        TheAnchor = tmpAnchor[1];
        if (TheAnchor) additionalURL = TheParams;

        tempArray = additionalURL.split('&');

        for (var i = 0; i < tempArray.length; i++) {
            if (tempArray[i].split('=')[0] != param) {
                newAdditionalURL += temp + tempArray[i];
                temp = '&';
            }
        }
    } else {
        var tmpAnchor = baseURL.split('#');
        var TheParams = tmpAnchor[0];
        TheAnchor = tmpAnchor[1];

        if (TheParams) baseURL = TheParams;
    }

    if (TheAnchor) paramVal += '#' + TheAnchor;

    var rows_txt = temp + '' + param + '=' + paramVal;
    return baseURL + '?' + newAdditionalURL + rows_txt;
}
